// src/utils/themeSync.ts
// 主题同步工具：负责前端 themeStore 与后端 preferences.theme 之间的同步

import useThemeStore from '@/store/useThemeStore';
import { useUserStore } from '@/store/useUserStore';
import { updatePreferences } from '@/apis/profile';

/**
 * 后端主题值 → 前端 isDarkMode 映射
 * @param theme 后端主题值
 * @returns 前端 isDarkMode boolean 值
 */
export const themeToBoolean = (theme: 'light' | 'dark' | 'system'): boolean => {
  switch (theme) {
    case 'light':
      return false;
    case 'dark':
      return true;
    case 'system':
      // 暂不支持 system，默认转换为 dark
      // 未来如需支持，可使用：window.matchMedia('(prefers-color-scheme: dark)').matches
      return true;
    default:
      return true; // 默认暗色
  }
};

/**
 * 前端 isDarkMode → 后端主题值映射
 * @param isDarkMode 前端 isDarkMode boolean 值
 * @returns 后端主题值
 */
export const booleanToTheme = (isDarkMode: boolean): 'light' | 'dark' => {
  return isDarkMode ? 'dark' : 'light';
};

/**
 * 从服务端加载主题并同步到 themeStore
 * 用于登录后、应用启动时从后端恢复用户的主题偏好
 * @param serverTheme 服务端返回的主题值
 */
export const loadThemeFromServer = (serverTheme: 'light' | 'dark' | 'system') => {
  const isDarkMode = themeToBoolean(serverTheme);
  const { setDarkMode } = useThemeStore.getState();
  setDarkMode(isDarkMode);
};

/**
 * 切换主题并同步到服务端
 * 使用乐观更新策略：先更新 UI，后台异步同步，失败时回滚
 * @returns Promise<boolean> 是否成功同步到服务端
 */
export const toggleThemeWithSync = async (): Promise<boolean> => {
  const { isDarkMode, toggleTheme, setDarkMode } = useThemeStore.getState();
  const { updatePreferences: updateUserPreferences } = useUserStore.getState();

  // 先获取新值（切换后的值）
  const newIsDarkMode = !isDarkMode;
  const newTheme = booleanToTheme(newIsDarkMode);

  // 立即更新 UI（乐观更新）
  toggleTheme();

  try {
    // 异步同步到后端
    await updatePreferences({ theme: newTheme });

    // 同步更新 userStore 中的 preferences
    updateUserPreferences({ theme: newTheme });

    return true;
  } catch (error) {
    console.error('Failed to sync theme to server:', error);

    // 失败时回滚 UI
    setDarkMode(isDarkMode);

    return false;
  }
};

export default {
  themeToBoolean,
  booleanToTheme,
  loadThemeFromServer,
  toggleThemeWithSync,
};
